/**
 * useRoom — connects to a SessionRoom over WebSocket and tracks live state.
 * Used by participant, presenter, and projection screens alike (each just
 * renders a different slice). Auto-reconnects with a short backoff.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { api, type ServerEvent, type PublicQuestion, type QuestionAggregate, type LeaderboardEntry, type MyBoard, type TeamStanding } from "./api.js";
import {
  afterDrain,
  clearFor,
  dropRefused,
  enqueue,
  hydrate,
  needsNonce,
  newNonce,
  persist,
  unsentCount,
  type Pending,
  type Queued,
} from "./queue.js";

export type RoundVerdict = "unknown" | "none" | "round";

export interface RoomState {
  connected: boolean;
  status: "draft" | "live" | "closed";
  participants: number;
  question: PublicQuestion | null;
  aggregate: QuestionAggregate | null;
  leaderboard: LeaderboardEntry[];
  /** Team standings from the same frame as the individual board (W7). Empty when there is no team layer. */
  teamBoard: TeamStanding[];
  /** THIS phone's team, from the one addressed frame that carries membership. Null when on none. */
  team: { id: string; name: string } | null;
  /** THIS phone's place in its team and the room. Kept across a new round, like the team itself. */
  myBoard: MyBoard | null;
  closed: boolean;
  /**
   * The id of the round the LAST `closed` frame named, reset by the next `question` frame (W12
   * fatia 3). `closed` alone says a round ended; this says WHICH — the one thing the planner needs
   * to tell "my block's round closed" from "the Presenter closed something else in this room", and
   * the only piece of the wire this hook was discarding. Additive: participant, presenter and
   * projection ignore it.
   */
  lastClosedQuestionId: string | null;
  /**
   * The verdict of the `state` frame on whether the room holds a round (THE-1438): `unknown` until
   * that frame arrives, `none` when it names no round (`currentQuestionId === null`, which is what a
   * Durable Object eviction leaves), `round` when it names one or a `question` frame arrives. The
   * planner arms from `/results` only on `none`: that payload carries no open round, so it cannot
   * say the room is quiet. Additive: participant, presenter and projection ignore it.
   */
  roundVerdict: RoundVerdict;
  lastError: string | null;
  /**
   * The room's join code (W10a), from the `state` frame. "" until the room reports one — which is
   * also what a pre-W10a room reports forever, so every consumer must render nothing rather than a
   * QR of an empty code.
   */
  joinCode: string;
  /** Trivia race progress, or null when this session is not running a race. */
  race: { round: number; total: number; ended: boolean } | null;
  /**
   * This phone's submissions for the OPEN round, kept so they can be replayed when the venue's
   * wifi comes back (W10b, US-LV-18). Non-empty for most of a round by design — items are not
   * removed on drain — so `Pending.sent`, not the length, is what may be said out loud.
   */
  pending: Pending[];
  /**
   * Four consecutive failed connects. Not `navigator.onLine`, which stays `true` in the failure
   * this wave is about; a plain failed-attempt counter is the only actionable signal the phone
   * behind a blocked WebSocket upgrade produces.
   */
  offline: boolean;
  /** The last connect was refused at the room's socket ceiling (US-LV-27); cleared by the first frame. */
  roomFull: boolean;
}

/** Mirrors `ClientMessage` in the API's `realtime/protocol.ts` — `qid`/`n` included (W10b). */
export type ClientMessage =
  | { t: "join"; token: string; nickname: string }
  // One tag, three answers (W9a) — which one is valid is the ROUND's decision, made server-side.
  | { t: "submit_mc"; optionId: string; qid?: string }
  | { t: "submit_mc"; weights: Record<string, number>; qid?: string }
  | { t: "submit_mc"; order: string[]; qid?: string }
  | { t: "submit_words"; text: string; qid?: string; n?: string }
  | { t: "submit_open"; text: string; qid?: string; n?: string }
  | { t: "upvote"; entryId: string; qid?: string }
  | { t: "submit_prompt"; text: string; qid?: string }
  | { t: "vote_prompt"; entryId: string; qid?: string };

/**
 * The queue was discarded because the round ended with answers still unsent. A CLIENT-only code: it
 * is minted here, rendered from the phone's own string table, and never travels in either
 * direction — so it is not in `SEAT_LOST` and can eject nobody.
 */
export const ROUND_CLOSED_UNSENT = "ROUND_CLOSED_UNSENT";

export const INITIAL: RoomState = {
  connected: false,
  status: "draft",
  participants: 0,
  question: null,
  aggregate: null,
  leaderboard: [],
  teamBoard: [],
  team: null,
  myBoard: null,
  closed: false,
  lastClosedQuestionId: null,
  roundVerdict: "unknown",
  lastError: null,
  joinCode: "",
  race: null,
  pending: [],
  offline: false,
  roomFull: false,
};

/**
 * The state a room starts from. A path is a room: moving to another one (or to none) must not carry
 * the old room's round, close, board or sentence into it — that is what made re-attaching the SAME
 * room inside one planner mount leave a live round un-announced (THE-1437).
 *
 * The participant's offline queue is NOT part of what is zeroed: it is re-read from the store under
 * the NEW path (`hydrate`), and the old path's queue stays in the store untouched, so coming back to
 * it finds it. An observer never reads or writes that store.
 */
export function roomForPath(connectPath: string | null, observer: boolean): RoomState {
  return connectPath && !observer ? { ...INITIAL, pending: hydrate(connectPath) } : INITIAL;
}

/** The state after the path went `prev` → `next`: a new room starts over, the same path is left alone. */
export function resetForPath(s: RoomState, prev: string | null, next: string | null, observer: boolean): RoomState {
  return prev === next ? s : roomForPath(next, observer);
}

// Reconnect backoff. A party race puts a room full of phones on flaky mobile data at the same moment;
// a flat retry makes every dropped client hammer the room in lockstep, so back off and add jitter to
// spread the reconnect storm.
const RETRY_BASE_MS = 500;
const RETRY_MAX_MS = 8_000;
/**
 * Give up on a socket that has not reached OPEN in six seconds (W10b, US-LV-18 AC6).
 *
 * During an outage a new WSS connect does not FAIL — it sits in `CONNECTING` through the whole
 * TCP/TLS timeout, and the retry is scheduled only from `onclose`. So a 60 s drop became 90 s with
 * the queue undrained. Closing it hands the retry to the jittered backoff that already exists.
 */
const CONNECT_TIMEOUT_MS = 6_000;
/** Consecutive failed connects before the phone says so out loud. */
const OFFLINE_AFTER_ATTEMPTS = 4;

/** Close code the live-api uses for an upgrade refused at the socket ceiling (US-LV-27). */
export const ROOM_FULL_CLOSE_CODE = 4001;

/** Wait before asking a full room again: 8 to 16 s, flat — a full room is not a failing one. */
export function roomFullRetryMs(random: number): number {
  return Math.round(8_000 + 8_000 * Math.min(Math.max(random, 0), 0.999999));
}

export interface RoomOptions {
  /** Re-sends `join` on every (re)connect. The credential, which is why it is never queued. */
  onOpen?: () => void;
  /**
   * The join response's `longRunning`: the queue is kept in the same store as the SEAT (round-1
   * review). A long-running seat lives in `localStorage`, so a queue left in `sessionStorage` died
   * with the tab while the credential it was minted under survived — the one combination that
   * loses an answer and keeps the identity.
   */
  persistent?: boolean;
  /**
   * This caller WATCHES the room and never answers it (the planner, the presenter, the projection).
   *
   * It turns the offline queue off entirely — no `hydrate`, no `persist`, no drain — and that is a
   * correctness fix, not a saving (round-1 review of W12 fatia 3). The queue's storage key is
   * `KEY + connectPath`, and an observer's `connectPath` is BYTE-IDENTICAL to a participant's for
   * the same room (`/sessions/:id/connect`). So a facilitator who joined their own room as a
   * participant to test it, left answers unsent, and then opened the planner in that browser had
   * the planner hydrate those frames, stand ready to drain them onto a socket that never sent
   * `join`, and overwrite the phone's queue from its own persist effect.
   *
   * An observer must therefore never call `send` or `clearQueue`; both are inert-by-construction
   * here (nothing can enter the queue, so nothing can drain), and the guards below say so once
   * rather than trusting every caller to keep not calling them.
   */
  observer?: boolean;
}

/**
 * Connect to a room and track its state. One hook for the participant, the presenter, the
 * projection and the planner; `observer` is what separates the screen that ANSWERS from the three
 * that only watch.
 */
export function useRoom(connectPath: string | null, options: RoomOptions = {}): {
  state: RoomState;
  /** `false` = refused, and the caller must KEEP what the participant typed (the cap, or no round). */
  send: (msg: ClientMessage) => boolean;
  /** Bin the queue — for the one caller that owns the seat, when the seat itself is gone. */
  clearQueue: () => void;
} {
  // Destructured to PRIMITIVES here: every dependency array below reads these, and an options
  // object literal is a new identity on every render — a re-connect per keystroke.
  const { onOpen, persistent = false, observer = false } = options;
  // The queue survives a reload of the same tab into the same room; see `hydrate` for its ceiling.
  const [state, setState] = useState<RoomState>(() => roomForPath(connectPath, observer));
  // A new path is a new room: derive the reset DURING render (React's "adjust state on prop
  // change" pattern) rather than in an effect, so no render ever pairs the new path with the old
  // room's state. Read by reasoning, not by test — this app has no jsdom.
  const [statePath, setStatePath] = useState(connectPath);
  if (statePath !== connectPath) {
    setStatePath(connectPath);
    setState((s) => resetForPath(s, statePath, connectPath, observer));
  }
  const wsRef = useRef<WebSocket | null>(null);
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  // The queue and the open round, mirrored out of state: `send` runs inside an event handler and
  // must not read a value React has not re-rendered with yet, and StrictMode double-invokes a state
  // updater — so a socket write may never live inside one.
  const pendingRef = useRef<Pending[]>(state.pending);
  const qidRef = useRef<string | null>(state.question?.id ?? null);

  useEffect(() => {
    pendingRef.current = state.pending;
    qidRef.current = state.question?.id ?? null;
    // Never for an observer: it would delete or overwrite a PARTICIPANT's queue under the same key.
    if (connectPath && !observer) persist(connectPath, state.pending, persistent);
  }, [state.pending, state.question, connectPath, persistent, observer]);

  useEffect(() => {
    if (!connectPath) return;
    let closedByUs = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;

    const connect = (): void => {
      const ws = new WebSocket(api.wsUrl(connectPath));
      wsRef.current = ws;
      const watchdog = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) ws.close();
      }, CONNECT_TIMEOUT_MS);
      // Is this connection still waiting to hear whether the queued round is the live one? Set at
      // `onopen` and cleared by the first frame that answers, so a drain happens at most once per
      // connection — see `catchUp`.
      let awaiting = false;
      ws.onopen = () => {
        clearTimeout(watchdog);
        attempt = 0; // a good connection resets the backoff
        awaiting = !observer && pendingRef.current.length > 0;
        if (closedByUs) return;
        onOpenRef.current?.(); // re-sends `join`, which is the credential and is never queued
        setState((s) => ({ ...s, connected: true, offline: false }));
      };
      /** Replay FIFO, straight to the socket rather than through `send` — which would re-stamp the
       *  frame with a new nonce and queue it a second time. */
      const drain = (): void => {
        try {
          for (const p of pendingRef.current) ws.send(JSON.stringify(p.frame));
        } catch {
          // The socket died mid-drain. Nothing is lost: items are never removed on drain, so what
          // is queued rides the next open.
          return;
        }
        pendingRef.current = afterDrain(pendingRef.current);
        setState((s) => ({ ...s, pending: afterDrain(s.pending) }));
      };
      ws.onclose = (ev: CloseEvent) => {
        clearTimeout(watchdog);
        // WE closed it — an unmount, or a `connectPath` that changed. Bail BEFORE the write, not
        // after: a detached socket's late `onclose` would land `connected: false` (and, after four
        // tries, `offline: true`) on the state the NEW room's socket is already using. Nothing a participant sees changes: `closedByUs`
        // is false for every unexpected close, which is the only kind they have.
        if (closedByUs) return;
        if (ev.code === ROOM_FULL_CLOSE_CODE) {
          // Not a failing connection, a full room: say so, keep the backoff where it was, ask again slowly.
          setState((s) => ({ ...s, connected: false, offline: false, roomFull: true }));
          retry = setTimeout(connect, roomFullRetryMs(Math.random()));
          return;
        }
        const failed = attempt + 1; // consecutive failed connects, this one included
        setState((s) => ({ ...s, connected: false, roomFull: false, offline: failed >= OFFLINE_AFTER_ATTEMPTS }));
        const capped = Math.min(RETRY_BASE_MS * 2 ** attempt, RETRY_MAX_MS);
        attempt += 1;
        retry = setTimeout(connect, capped / 2 + Math.random() * (capped / 2));
      };
      ws.onmessage = (ev: MessageEvent) => {
        // A detached socket can still deliver a frame already in flight; with the state now reset per
        // path it would land in the NEW room's state.
        if (closedByUs) return;
        let event: ServerEvent;
        try {
          event = JSON.parse(ev.data as string) as ServerEvent;
        } catch {
          return;
        }
        // The catch-up decides the queue's fate BEFORE the reducer sees the frame, because only the
        // socket can replay and only the frame can say whether the round is still there. A "clear"
        // needs nothing here: the reducer's own `state`/`question` arms discard it and mint the
        // sentence, so the phone learns the round ended without a round-trip to be refused by.
        if (awaiting) {
          const verdict = catchUp(pendingRef.current[0]?.frame.qid ?? null, event);
          if (verdict !== "wait") awaiting = false;
          if (verdict === "drain") drain();
        }
        setState((s) => (s.roomFull ? reduce({ ...s, roomFull: false }, event) : reduce(s, event)));
      };
    };
    connect();

    /**
     * Reconnect NOW rather than on a timer the OS never fired (AC6). A phone that slept through the
     * outage had its timers throttled or frozen; only a socket already CLOSED is woken here,
     * because a CLOSING one is about to run `onclose` and start the backoff itself — waking both
     * would put two connect chains on one room.
     */
    const wake = (): void => {
      const ws = wsRef.current;
      if (closedByUs || !ws || ws.readyState !== WebSocket.CLOSED) return;
      if (retry) clearTimeout(retry);
      connect();
    };
    const onVisible = (): void => {
      if (document.visibilityState === "visible") wake();
    };
    const hasDom = typeof document !== "undefined" && typeof window !== "undefined";
    if (hasDom) {
      document.addEventListener("visibilitychange", onVisible);
      window.addEventListener("online", wake);
    }

    return () => {
      closedByUs = true;
      if (retry) clearTimeout(retry);
      if (hasDom) {
        document.removeEventListener("visibilitychange", onVisible);
        window.removeEventListener("online", wake);
      }
      wsRef.current?.close();
    };
  }, [connectPath, observer]);

  const send = useCallback((msg: ClientMessage): boolean => {
    const ws = wsRef.current;
    const open = ws !== null && ws.readyState === WebSocket.OPEN;
    // `join` is a credential, not an answer: `onOpen` re-sends it on every reconnect, so queueing it
    // would replay a stale identity frame at the worst possible moment.
    if (msg.t === "join") {
      if (open && ws) ws.send(JSON.stringify(msg));
      return open;
    }
    // Nothing open means nothing to stamp, and an unstamped frame is one the round guard would wave
    // into whatever round opens next.
    const qid = qidRef.current;
    if (qid === null) return false;
    const frame = { ...msg, qid, ...(needsNonce(msg.t) ? { n: newNonce() } : {}) } as Queued;
    const { queue, full } = enqueue(pendingRef.current, frame, open);
    // SENT and also queued, always: at tap time an OPEN socket is not evidence of a working uplink.
    // At the cap it is neither — refused, which the caller must not confuse with delivered: the
    // typed text stays in the box, because `false` is the promise that nothing was taken.
    if (full) {
      setState((s) => (s.lastError === null ? s : { ...s, lastError: null }));
      return false;
    }
    if (open && ws) ws.send(JSON.stringify(frame));
    pendingRef.current = queue;
    // Clear the previous refusal on every attempt: server rejections are the only thing that sets
    // lastError, and nothing acknowledges success, so a sticky error would outlive a fixed retry.
    setState((s) => (s.pending === queue && s.lastError === null ? s : { ...s, pending: queue, lastError: null }));
    return true;
  }, []);

  const clearQueue = useCallback(() => {
    pendingRef.current = [];
    // Written HERE, not left to the persist effect: the only caller is seat loss, which unmounts
    // this component in the same batch — the effect never runs, and the next token in the same tab
    // would hydrate the dead seat's frames and replay them as a different person's answers.
    // Both stores are cleared by `persist` on an empty queue, so a seat given up leaves nothing
    // behind in the store it was not using.
    if (connectPath && !observer) persist(connectPath, [], persistent);
    setState((s) => (s.pending.length === 0 ? s : { ...s, pending: [] }));
  }, [connectPath, persistent, observer]);

  return { state, send, clearQueue };
}

/**
 * The code a surface may advertise — none once the room stops taking people (W10a review, F1/G1).
 *
 * A closed session's code stops resolving server-side (`findActiveByCode` excludes it), but the
 * `state` frame keeps carrying it beside `status: "closed"`, so the client is the only place that
 * can decide this. Returning "" rather than a flag is what makes it one rule per surface:
 * `JoinCard` already draws nothing for an empty code, so a placement inherits the gate for free.
 *
 * The two surfaces need OPPOSITE defaults, which is why they live in one function — the asymmetry
 * is the interesting part and it should be readable in one place:
 *
 * - `"wall"` (projection) is strict: `live` or nothing. It has no source of truth but the socket,
 *   so while `status` is still `draft` it genuinely does not know whether the room takes people,
 *   and a wall is the surface a stranger scans from across a room.
 * - `"host"` (presenter) is permissive: anything but `closed`. The host's screen has a
 *   WS-INDEPENDENT source — the session was created `live` by the REST call that returned this
 *   code — so gating it on the socket meant a room whose WebSocket upgrade never completes (a
 *   corporate proxy that passes HTTPS and blocks the upgrade, which is our ICP; or a 503 at the
 *   socket ceiling) left the host with no QR at all for a session that was running fine.
 */
export function advertisableCode(
  status: RoomState["status"],
  joinCode: string,
  surface: "wall" | "host",
): string {
  if (status === "closed") return "";
  return surface === "host" || status === "live" ? joinCode : "";
}

/**
 * Exported for one reason: this is where a reconnect's frame ORDER turns into what the screen shows,
 * and the `question` arm resets `closed` — so which frames the server replays on catch-up is a rule
 * worth pinning without a socket. (`canDerive` reads exactly the two fields it produces.)
 */
/**
 * What the first frame after a reconnect says about the round the queue is holding answers for
 * (W10b, US-LV-18 AC4) — the decision, without the socket, so it has a test.
 *
 * Draining in `onOpen` was wrong: it fired before the catch-up said which round the room is in, so
 * a reconnect into a round that had moved on spent a round-trip per item to be told `WRONG_ROUND`,
 * by which time the queue was marked sent and the "a rodada fechou" sentence could no longer be
 * minted. The phone said nothing at all. The client already holds everything it needs to compare.
 *
 * - `"drain"` — the catch-up is replaying the same round (the room always re-sends the live one), so
 *   the queued frames are for a round that is still taking answers: replay them.
 * - `"clear"` — the room reports a different round, or no round at all: they are stale, and the
 *   reducer's own arms discard them and say so. Nothing is sent, so nothing is refused.
 * - `"wait"` — this frame carries no verdict (`joined`, `aggregate`, a `state` for the same round
 *   whose `question` is still on its way).
 */
export function catchUp(queuedQid: string | null, e: ServerEvent): "drain" | "clear" | "wait" {
  if (queuedQid === null) return "wait";
  if (e.t === "question") return e.question.id === queuedQid ? "drain" : "clear";
  if (e.t === "state") return e.currentQuestionId === queuedQid ? "wait" : "clear";
  return "wait";
}

/** The queue is for a round the room is no longer in. Discard it, and say so if anything is unsent. */
function stale(s: RoomState): Pick<RoomState, "pending" | "lastError"> {
  return { pending: [], lastError: unsentCount(s.pending) > 0 ? ROUND_CLOSED_UNSENT : null };
}

export function reduce(s: RoomState, e: ServerEvent): RoomState {
  switch (e.t) {
    case "state": {
      const next: RoomState = {
        ...s,
        status: e.status,
        participants: e.participants,
        joinCode: e.joinCode,
        roundVerdict: e.currentQuestionId === null ? "none" : "round",
      };
      // The room names the round it can REPLAY on every `state` frame (a closed round restored after
      // an eviction comes back as none), so the comparison belongs to the `question` frame that then
      // always follows it on a catch-up: emptying the queue here would leave
      // that frame nothing to compare, and its own arm would wipe the sentence minted one frame
      // earlier — nothing paints between the two. The one verdict only THIS frame can give is
      // "no round at all", which no `question` frame will ever announce.
      if (s.pending.length > 0 && e.currentQuestionId === null) return { ...next, ...stale(s) };
      return next;
    }
    case "joined":
    case "left":
      return { ...s, participants: e.participants };
    case "question": {
      // The queue holds items for exactly one round. A DIFFERENT id means that round has gone; the
      // SAME id is the reconnect catch-up replaying the live round, and clearing there would
      // silently defeat the whole wave (W10b, US-LV-18 AC4).
      const moved = s.pending.some((p) => p.frame.qid !== e.question.id);
      // The team standings share the individual board's lifecycle — they are one picture, hidden
      // while a round runs. `team` and `myBoard` deliberately survive: which table you are at is not
      // a per-round fact, and blanking your rank at every question would make it unreadable.
      return {
        ...s,
        question: e.question,
        roundVerdict: "round",
        aggregate: null,
        closed: false,
        // A new round is open: the previous close is history, and leaving its id up would let a
        // consumer act on it twice (the planner re-arms off a value that only ever changes).
        lastClosedQuestionId: null,
        leaderboard: [],
        teamBoard: [],
        // `stale` says the sentence only for answers that never reached a socket: a queue is
        // non-empty for most of a normal round, so saying it on length alone would cry wolf.
        ...(moved ? stale(s) : { pending: s.pending, lastError: null }),
      };
    }
    case "aggregate":
      return { ...s, aggregate: e.aggregate };
    case "closed": {
      const ended = { ...s, closed: true, lastClosedQuestionId: e.questionId };
      if (!s.pending.some((p) => p.frame.qid === e.questionId)) return ended;
      // The round is over: nothing queued for it is recoverable any more, so it goes — and it goes
      // out loud when something in it never left the phone. The trust-killer is the silence.
      return { ...ended, ...stale(s) };
    }
    case "leaderboard":
      // No `teams` on the frame means the room has no team layer — which is how this client learns
      // one was CLEARED, without a second event that would have to be about membership.
      return { ...s, leaderboard: e.entries, teamBoard: e.teams ?? [], ...(e.teams ? {} : { team: null, myBoard: null }) };
    case "team":
      return { ...s, team: { id: e.id, name: e.name } };
    case "my_board":
      return {
        ...s,
        myBoard: {
          teamRank: e.teamRank,
          teamCount: e.teamCount,
          rankInTeam: e.rankInTeam,
          teamSize: e.teamSize,
          globalRank: e.globalRank,
        },
      };
    case "race":
      return { ...s, race: { round: e.round, total: e.total, ended: e.ended } };
    case "error":
      // The server's safety net for a replay the client's own comparison did not catch. It is
      // rendered as the SENTENCE, not as the code: `WRONG_ROUND` reaches the screen only through
      // `promptVoteError`'s table, so a raw code would say "esta rodada já terminou" over a live
      // input on every other round type — and say nothing at all on the ones that do not render it.
      if (e.code === "WRONG_ROUND" || e.code === "ROUND_CLOSED") {
        return { ...s, pending: [], lastError: ROUND_CLOSED_UNSENT };
      }
      // One refusal must not bin the round's other seven — see `clearFor` for which ones do — and
      // must not leave the refused frame COUNTING, which is `dropRefused` (round-1 review, item I).
      // Composed rather than merged: one answers "does the seat survive", the other "did this frame
      // land", and a code can be either without being both.
      return { ...s, lastError: e.code, pending: dropRefused(clearFor(s.pending, e.code), e.code) };
    default:
      return s;
  }
}
